import {
  IOrder, IOrderEditRequest, IOrderItem, IRefundRequest, IRefundSaveResponse, IResendEmailResponse,
} from '../model/IOrder';
import { IOrderCategorySummary } from '../model/IOrderSummary';
import { IPageParams } from './IApi';
import { IPagesResponse } from './IPagesResponse';
import { buildOrderSearchParams, IOrderFilterState } from './orderFilter';
import api from './api';

// --
// -- What a PATCH may carry. The server reads carrier/trackingNum per item,
// -- not the siCarrier/siTrackingNum it sends back, so those two are excluded
// -- here to stop anyone sending the read shape by mistake.
// --
export type IPatchOrderItemInfo = Pick<IOrderItem, 'id'> &
  Omit<Partial<IOrderItem>, 'siCarrier' | 'siTrackingNum'> & {
    carrier?: string;
    trackingNum?: string;
  };

export type IPatchOrderInfo = Pick<IOrder, 'orderId'> &
  Omit<Partial<IOrder>, 'orderItems' | 'siCarrier' | 'siTrackingNum'> & {
    orderItems?: IPatchOrderItemInfo[];
  };

/** Which of the order's emails to send again. */
export type TResendKind = 'partscare-offer' | 'shipping-protection' | 'vendor';

/** What both order reports take: a page, and the date range they cover. */
export interface IOrdersReportParams {
  page?: number;
  size?: number;
  sort?: string;
  sortDir?: 'asc' | 'desc';
  createdFrom?: string;
  createdTill?: string;
}

export const orderApi = api.injectEndpoints({
  endpoints: (build) => ({
    // --
    // -- OrderManagementController#searchOrder. Filter fields map to
    // -- OrderFilter; Pageable takes page/size/sort as `field,dir`.
    // --
    // -- A vendor's results are narrowed to their own supplier keys by the
    // -- backend, from the signed-in principal - never by anything sent here.
    // --
    getOrderList: build.query<IPagesResponse<IOrder[]>, IPageParams & IOrderFilterState>({
      query: (args) => ({
        url: 'ordermanagement/search',
        params: buildOrderSearchParams(args),
      }),
      providesTags: ['Orders'],
    }),

    // --
    // -- The same search, as a CSV file rather than a page of it.
    // --
    // -- A query so it goes through the shared base query and picks up the
    // -- refresh-on-401; the response is a blob, so RTK is told not to try to
    // -- parse it as JSON. Paging is not sent - the server exports the whole
    // -- filtered set, which is the point of the download.
    // --
    downloadOrdersCsv: build.query<Blob, Omit<IPageParams, 'page' | 'size'> & IOrderFilterState>({
      query: (args) => {
        const params = buildOrderSearchParams({ ...args, page: 0, size: 0 });
        // -- Not sent at all. The server exports the whole filtered set, and
        // -- a size of 0 would only confuse the Pageable binding on the way.
        delete params.page;
        delete params.size;

        return {
          url: 'ordermanagement/search/csv',
          params,
          responseHandler: (response: Response) => response.blob(),
          cache: 'no-cache' as RequestCache,
        };
      },
    }),

    // --
    // -- OrderManagementController#updateOrder.
    // --
    // -- Saving tracking is not only a write: the server stamps siDate, moves
    // -- the item to Shipped and emails the customer a shipping confirmation.
    // -- Invalidating 'Order' refetches the page so what is on screen is what
    // -- the server decided, rather than what was typed.
    // --
    patchOrder: build.mutation<IOrder, IPatchOrderInfo>({
      query: ({ orderId, ...body }) => ({
        url: `ordermanagement/${orderId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Order', 'Orders'],
    }),

    // --
    // -- Unshipped Orders: processed, not yet shipped.
    // --
    // -- Backed by a JPA Specification, so Pageable sorting works and the
    // -- column headers are sortable. Dates go as a pair or not at all -
    // -- half a range is not a filter the backend understands.
    // --
    getUnshippedOrders: build.query<IPagesResponse<IOrder[]>, IOrdersReportParams>({
      query: ({ sort, sortDir, createdFrom, createdTill, ...params }) => ({
        url: 'ordermanagement/unshipped_orders_report',
        params: {
          ...params,
          ...(createdFrom && createdTill ? { createdFrom, createdTill } : {}),
          ...(sort ? { sort: `${sort},${sortDir || 'asc'}` } : {}),
        },
      }),
      providesTags: ['Orders'],
    }),

    downloadUnshippedOrdersCsv: build.query<Blob, Omit<IOrdersReportParams, 'page' | 'size'>>({
      query: ({ sort, sortDir, createdFrom, createdTill }) => ({
        url: 'ordermanagement/unshipped_orders_report/csv',
        params: {
          ...(createdFrom && createdTill ? { createdFrom, createdTill } : {}),
          ...(sort ? { sort: `${sort},${sortDir || 'asc'}` } : {}),
        },
        responseHandler: (response: Response) => response.blob(),
        cache: 'no-cache' as RequestCache,
      }),
    }),

    // --
    // -- Late Orders: past the ship window for their part type.
    // --
    // -- No sort is ever sent. This one is a native query that carries its own
    // -- ORDER BY, and Spring appends a Pageable sort to the raw SQL using the
    // -- property names as written - "orderId" is not a column, so sorting
    // -- fails at the database rather than being ignored. The CS portal drops
    // -- sort here for the same reason.
    // --
    // -- createdFrom and createdTill are required by the endpoint, not
    // -- optional as on the unshipped report.
    // --
    getLateOrders: build.query<IPagesResponse<IOrder[]>, IOrdersReportParams>({
      query: ({ sort, sortDir, ...params }) => ({
        url: 'ordermanagement/late_orders_report',
        params,
      }),
      providesTags: ['Orders'],
    }),

    downloadLateOrdersCsv: build.query<Blob, Omit<IOrdersReportParams, 'page' | 'size'>>({
      query: ({ createdFrom, createdTill }) => ({
        url: 'ordermanagement/late_orders_report/csv',
        params: { createdFrom, createdTill },
        responseHandler: (response: Response) => response.blob(),
        cache: 'no-cache' as RequestCache,
      }),
    }),

    // --
    // -- Refunds. Admin only on the server, and under ordermanagement rather
    // -- than /qbo/refunds - that path is behind an HTTP Basic filter for the
    // -- third-party pusher and would never see our session.
    // --
    // -- Saving also triggers the QBO push server side; the response says what
    // -- it did. No delete endpoint exists, by design.
    // --
    addRefund: build.mutation<IRefundSaveResponse, { orderId: string } & IRefundRequest>({
      query: ({ orderId, ...body }) => ({
        url: `ordermanagement/${orderId}/refunds`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Order', 'Orders'],
    }),

    updateRefund: build.mutation<IRefundSaveResponse, { orderId: string, refundId: number } & IRefundRequest>({
      query: ({ orderId, refundId, ...body }) => ({
        url: `ordermanagement/${orderId}/refunds/${refundId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Order', 'Orders'],
    }),

    // --
    // -- Resending an order's emails. Admin only on the server, and the screen
    // -- confirms first - these go to real customers and vendors.
    // --
    // -- The same conditions the new-order listeners apply: an order that does
    // -- not qualify comes back { sent: false, message } rather than as an
    // -- error, so the UI reports it rather than looking broken.
    // --
    resendOrderEmail: build.mutation<IResendEmailResponse, { orderId: string, kind: TResendKind }>({
      query: ({ orderId, kind }) => ({
        url: `ordermanagement/${orderId}/emails/${kind}`,
        method: 'POST',
      }),
    }),

    // --
    // -- Editing the order. The same PATCH the tracking form uses; the server
    // -- strips these fields for anyone who is not an admin or an agent.
    // --
    updateOrderDetails: build.mutation<IOrder, { orderId: string } & IOrderEditRequest>({
      query: ({ orderId, ...body }) => ({
        url: `ordermanagement/${orderId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Order', 'Orders'],
    }),

    getOrder: build.query<IOrder, string>({
      query: (orderId) => ({
        url: `ordermanagement/order/${orderId}`,
      }),
      providesTags: ['Order'],
    }),

    // --
    // -- Orders grouped by the day they were created. Not paged - it is one
    // -- row per day and only reads as a whole.
    // --
    getOrdersByDate: build.query<IOrderCategorySummary[], { createdFrom: string, createdTill: string }>({
      query: ({ createdFrom, createdTill }) => ({
        url: 'ordermanagement/summary/by_date',
        params: {
          ...(createdFrom ? { createdFrom } : {}),
          ...(createdTill ? { createdTill } : {}),
        },
      }),
      providesTags: ['Orders'],
    }),

    // -- Orders grouped by vendor, over the same columns.
    getOrdersByVendor: build.query<IOrderCategorySummary[], { createdFrom: string, createdTill: string }>({
      query: ({ createdFrom, createdTill }) => ({
        url: 'ordermanagement/summary/by_vendor',
        params: {
          ...(createdFrom ? { createdFrom } : {}),
          ...(createdTill ? { createdTill } : {}),
        },
      }),
      providesTags: ['Orders'],
    }),

    // -- Orders grouped by the vehicle their items are for.
    getOrdersByVehicle: build.query<IOrderCategorySummary[], { createdFrom: string, createdTill: string }>({
      query: ({ createdFrom, createdTill }) => ({
        url: 'ordermanagement/summary/by_vehicle',
        params: {
          ...(createdFrom ? { createdFrom } : {}),
          ...(createdTill ? { createdTill } : {}),
        },
      }),
      providesTags: ['Orders'],
    }),

    // -- Orders grouped by part type - the item's name.
    getOrdersByPartType: build.query<IOrderCategorySummary[], { createdFrom: string, createdTill: string }>({
      query: ({ createdFrom, createdTill }) => ({
        url: 'ordermanagement/summary/by_part_type',
        params: {
          ...(createdFrom ? { createdFrom } : {}),
          ...(createdTill ? { createdTill } : {}),
        },
      }),
      providesTags: ['Orders'],
    }),

    // -- Orders grouped by status, over the by-date columns.
    getOrdersByStatus: build.query<IOrderCategorySummary[], { createdFrom: string, createdTill: string }>({
      query: ({ createdFrom, createdTill }) => ({
        url: 'ordermanagement/summary/by_status',
        params: {
          ...(createdFrom ? { createdFrom } : {}),
          ...(createdTill ? { createdTill } : {}),
        },
      }),
      providesTags: ['Orders'],
    }),

    // -- Orders grouped by how they were paid for, by stored payment code.
    getOrdersByPaymentType: build.query<IOrderCategorySummary[], { createdFrom: string, createdTill: string }>({
      query: ({ createdFrom, createdTill }) => ({
        url: 'ordermanagement/summary/by_payment_type',
        params: {
          ...(createdFrom ? { createdFrom } : {}),
          ...(createdTill ? { createdTill } : {}),
        },
      }),
      providesTags: ['Orders'],
    }),

    // -- Orders grouped by DMA - the market area the shipping zip sits in.
    getOrdersByDma: build.query<IOrderCategorySummary[], { createdFrom: string, createdTill: string }>({
      query: ({ createdFrom, createdTill }) => ({
        url: 'ordermanagement/summary/by_dma',
        params: {
          ...(createdFrom ? { createdFrom } : {}),
          ...(createdTill ? { createdTill } : {}),
        },
      }),
      providesTags: ['Orders'],
    }),

    // -- Orders grouped by the state they shipped to.
    getOrdersByState: build.query<IOrderCategorySummary[], { createdFrom: string, createdTill: string }>({
      query: ({ createdFrom, createdTill }) => ({
        url: 'ordermanagement/summary/by_state',
        params: {
          ...(createdFrom ? { createdFrom } : {}),
          ...(createdTill ? { createdTill } : {}),
        },
      }),
      providesTags: ['Orders'],
    }),

    // -- Orders grouped by the site they came from.
    getOrdersByWebsite: build.query<IOrderCategorySummary[], { createdFrom: string, createdTill: string }>({
      query: ({ createdFrom, createdTill }) => ({
        url: 'ordermanagement/summary/by_website',
        params: {
          ...(createdFrom ? { createdFrom } : {}),
          ...(createdTill ? { createdTill } : {}),
        },
      }),
      providesTags: ['Orders'],
    }),
  }),
});
